import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Sample, samplesConstants } from "../api/Samples";
import { CustomTypedEntityFormProps } from "../common/entity/EntityInterfaces";
import { InputFormField } from "../common/formfields/InputFormField";
import { ProjectsVirtualizedSelectForm } from "../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { FormButtons } from "../common/forms/FormButtons";
import { FormHeader } from "../common/forms/FormHeader";
import * as yup from "yup";
import styles from "../common/forms/forms.module.css";
import {
  EntityCustomTypeForm,
  useCustomTypeForm,
  useCustomTypeResolver,
} from "../Customization/CustomTypes/generics/useCustomTypeForm";
import { calculateDirtyFields } from "../common/forms/utils/FormUtils";

export const sampleFormBaseSchema = {
  name: yup.string().required("A sample name is required"),
  projects: yup.array().ensure().min(1, "At least one project is required"),
};

export const SampleForm = ({
  event,
  fieldLabels,
  permissions,
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
  typeId,
  typesOverride,
}: CustomTypedEntityFormProps<"samples">) => {
  const { typedResolver } = useCustomTypeResolver<Partial<Sample>>(sampleFormBaseSchema);

  const {
    reset,
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    trigger,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<Partial<Sample>>({
    defaultValues: initialValues,
    resolver: typedResolver,
    shouldUnregister: false,
  });

  const { processCustomFields, types } = useCustomTypeForm({
    control,
    reset,
    getValues,
    entityType: "Sample",
    typeId,
    typesOverride,
  });

  // Helper function to process some fields
  const processEntity = useCallback((entity: Partial<Sample>) => {
    return {
      ...entity,
      customType: entity.customType ?? null,
      ...(!entity.customType && { customFields: {} }),
    } as Partial<Sample>;
  }, []);

  return (
    <>
      <FormHeader title={title} subtitle={subtitle} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">Details</legend>
        </fieldset>

        <InputFormField
          id={"name"}
          label={fieldLabels.name}
          errors={errors}
          register={register}
          autoFocus={true}
          required={true}
        />

        <ProjectsVirtualizedSelectForm
          id={"projects"}
          label={fieldLabels.projects}
          control={control}
          required
          isMulti
          horizontal
          allowCreateEntity
          showControls
          filters={{ currentUserHasAddPermission: true }}
        />

        {/* Render custom type */}
        <EntityCustomTypeForm
          entityType="Sample"
          types={types}
          control={control}
          setValue={setValue}
          initialValues={initialValues}
          errors={errors}
          entityConstants={samplesConstants}
        />

        <FormButtons
          onClose={onCancel}
          onSubmit={handleSubmit(async (entity) => {
            await trigger();
            await onSubmit(processCustomFields(processEntity(entity)));
          })}
          disabled={isSubmitting}
          errors={errors}
          loading={loading}
          submitButtonLabel={submitButtonLabel || "Save changes"}
          reset={["EDIT", "CLONE"].includes(event) ? () => reset(initialValues) : undefined}
          dirtyFields={["EDIT", "CLONE"].includes(event) ? calculateDirtyFields(dirtyFields) : undefined}
        />
      </form>
    </>
  );
};
